/**
 * HTTP client for mlist-api (JWT Bearer auth).
 * Used only for read-only admin-tools bridge endpoints.
 */

import { getMlistAccessToken } from "./mlistAuth";
import {
  getMlistApiConfig,
  isMlistApiConfigured,
  MLIST_API_BASE_URL,
} from "./mlistConfig";

/**
 * @param {string} path - Path relative to base (e.g. "admin-tools/listings/1001/owner/")
 * @param {RequestInit} [options] - fetch options
 * @returns {Promise<Response>}
 */
export async function mlistFetch(path, options = {}) {
  if (!isMlistApiConfigured()) {
    const err = new Error(
      "REACT_APP_MLIST_API_BASE_URL is not set; cannot call mlist-api"
    );
    err.code = "mlist_api_not_configured";
    throw err;
  }

  const config = getMlistApiConfig();
  if (!config.valid || !config.apiBaseUrl) {
    const err = new Error(
      config.errorMessage || "Invalid MList API base URL"
    );
    err.code = "mlist_api_invalid_url";
    throw err;
  }

  const base = config.apiBaseUrl || MLIST_API_BASE_URL;
  const url = path.startsWith("http") ? path : base + path;
  const method = (options.method || "GET").toUpperCase();

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");

  const token = getMlistAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  return fetch(url, { ...options, method, headers, credentials: "omit" });
}

/**
 * Same as mlistFetch but parse JSON and reject on non-ok by default.
 * @returns {Promise<{ data: any, response: Response }>}
 */
export async function mlistFetchJson(path, options = {}) {
  const response = await mlistFetch(path, options);
  const contentType = response.headers.get("content-type");
  const data =
    contentType && contentType.includes("application/json")
      ? await response.json()
      : null;

  if (!response.ok) {
    const err = new Error(
      data?.detail || data?.message || `HTTP ${response.status}`
    );
    err.status = response.status;
    err.data = data;
    err.response = response;
    throw err;
  }

  return { data, response };
}

export {
  getMlistApiConfig,
  isMlistApiConfigured,
  isMlistApiValid,
  MLIST_API_BASE_URL,
  MLIST_API_TOKEN_URL,
} from "./mlistConfig";

